import { HiMail } from 'react-icons/hi';
import { Seo } from '../../core/seo';

const TaxPolicy = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Tax Policy"
        description="HirePanther Tax Policy: how we handle Canadian GST/HST/QST on platform transactions."
        canonicalPath="/tax-policy"
      />
      <div className="max-w-4xl mx-auto bg-white p-8">
        {/* Page Title */}
        <h1 className="text-3xl font-bold text-[#284F60] mb-4">
          Tax Policy (Canada GST/HST/QST)
        </h1>

        {/* Effective Dates */}
        <p className="text-sm text-gray-500 mb-8">
          <strong>Effective Date:</strong> December 01, 2025 &nbsp; | &nbsp;
          <strong>Last Updated:</strong> December 01, 2025
        </p>

        {/* Intro */}
        <p className="mb-6 leading-relaxed">
          This Tax Policy explains how <strong>HirePanther Inc.</strong>{' '}
          (“HirePanther,” “we,” “our,” “us”) handles Canadian sales taxes on
          transactions made through our platform. By using HirePanther, you
          agree to this Policy in addition to our{' '}
          <a href="/terms-of-service" className="text-blue-600 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" className="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          .
        </p>

        {/* Sections */}
        <div className="space-y-8">
          {/* 1. Applicable Taxes */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              1. Applicable Taxes
            </h2>
            <p className="mb-3">
              In Canada, most goods and services are subject to sales tax.
              Depending on your province or territory, this may be:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Goods and Services Tax (GST)</strong> – 5% (federal).
              </li>
              <li>
                <strong>Harmonized Sales Tax (HST)</strong> – 13% or 15%
                (combined federal & provincial).
              </li>
              <li>
                <strong>Provincial Sales Tax (PST)</strong> – 6%–7% (in
                provinces that apply PST separately).
              </li>
              <li>
                <strong>Québec Sales Tax (QST)</strong> – 9.975% (applies in
                Québec).
              </li>
            </ul>
            <p className="mt-3">
              The applicable tax is determined by the customer’s province of
              residence or where the service is performed.
            </p>
          </section>

          {/* 2. How Taxes Are Collected */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              2. How Taxes Are Collected
            </h2>
            <p className="font-medium">For Customers:</p>
            <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
              <li>Taxes are calculated and displayed at checkout.</li>
              <li>
                You will see the tax amount itemized on your receipt/invoice.
              </li>
            </ul>
            <p className="font-medium">For Providers:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>HirePanther acts as a marketplace facilitator.</li>
              <li>
                Where required by law, we collect and remit GST/HST/QST on your
                behalf.
              </li>
              <li>Your payouts reflect earnings net of applicable taxes.</li>
              <li>
                In certain cases, Providers remain responsible for
                collecting/remitting provincial taxes (e.g., standalone PST in
                BC, SK, MB).
              </li>
            </ul>
          </section>

          {/* 3. Provider Responsibilities */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              3. Provider Responsibilities
            </h2>
            <p>
              Providers are independent businesses. You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
              <li>
                Registering for a GST/HST/QST account with the CRA or Revenu
                Québec if required by law.
              </li>
              <li>
                Providing HirePanther with your Business Number (BN) or Tax ID.
              </li>
              <li>Issuing tax-compliant invoices where applicable.</li>
              <li>Keeping your tax registration information up to date.</li>
              <li>
                Failure to comply may result in suspension or withholding of
                payouts.
              </li>
            </ul>
          </section>

          {/* 4. Invoicing */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              4. Invoicing
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Customers receive an invoice/receipt showing service cost,
                applicable taxes, and total paid.
              </li>
              <li>
                Providers receive a payout statement showing gross earnings,
                applicable taxes collected, HirePanther fees, and net payout.
              </li>
            </ul>
          </section>

          {/* 5. Remittance to Authorities */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              5. Remittance to Authorities
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                HirePanther remits collected GST/HST to the Canada Revenue
                Agency.
              </li>
              <li>
                HirePanther remits collected QST to Revenu Québec where
                applicable.
              </li>
              <li>
                For PST provinces (e.g., BC, SK, MB), Providers may be
                responsible for remitting directly.
              </li>
            </ul>
          </section>

          {/* 6. Tax Exemptions */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              6. Tax Exemptions
            </h2>
            <p>
              Certain customers or services may be exempt from sales tax (e.g.,
              some healthcare or educational services). Providers must ensure
              they are authorized to claim exemptions.
            </p>
            <p className="mt-2">
              Proof of exemption must be supplied in writing and may be subject
              to review.
            </p>
          </section>

          {/* 7. Cross-Border Transactions */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              7. Cross-Border Transactions
            </h2>
            <p>
              If you are outside Canada but book a service performed in Canada,
              Canadian GST/HST/QST may still apply.
            </p>
            <p className="mt-2">
              If you are outside Canada and provide services into Canada, you
              may also have registration and reporting obligations.
            </p>
          </section>

          {/* 8. Updates */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              8. Updates to This Policy
            </h2>
            <p>
              Tax laws change frequently. HirePanther may update this Policy to
              reflect new legislation or regulatory guidance. Updates will
              always be posted at{' '}
              <span className="font-medium">/legal/tax-policy</span> with a new
              effective date.
            </p>
          </section>

          {/* 9. Contact Us */}
          <section>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-3">
              9. Contact Us
            </h2>
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center space-x-3">
                <HiMail className="text-[#2D617A] text-2xl" />
                <a
                  href="mailto:tax@hirepanther.ca"
                  className="text-[#2D617A] hover:underline text-lg"
                >
                  tax@hirepanther.ca
                </a>
              </div>

              <p className="text-[#284F60] mt-3">
                You may also consult the Canada Revenue Agency (CRA) or Revenu
                Québec for official tax guidance.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TaxPolicy;
